// Menyu kutubxonasi: tayyor taomlarning UMUMIY rasmlari (barcha restoranlar uchun bitta nusxa).
// Rasm qo'yilsa — kutubxonadan qo'shgan va o'z rasmini qo'ymagan restoranlarda ham darhol chiqadi.
// "Lavash'dan yangilash" (Q-053): rozilik bergan restoranning ONE POS'dagi rasmlari NUSXA qilinadi — u restoranda
// hech narsa o'zgarmaydi. Oldingi rasm saqlanadi: "Oldingisi" bir bosishda qaytaradi.
// Guruh rasmi va kategoriya fon rasmi (Q-054): "grp_<guruh>", "cat_<kategoriya>" — kutubxonadan qo'shgan
// restoranlarga o'zi tushadi, ega o'zinikiga almashtira oladi.
import { useMemo, useRef, useState } from 'react';
import {
  LIBRARY_CATEGORIES,
  LIBRARY_GROUPS,
  LIBRARY_ITEMS,
  LIBRARY_PACKS,
  LIBRARY_SOURCE_PRODUCTS,
  LIBRARY_SOURCE_RESTAURANT,
  libraryCategory,
  libraryCategoryImageKey,
} from '@onepos/shared';
import { Alert, Badge, Button, Card, Icon, Modal, Spinner, Tabs, useAsync } from '@onepos/ui';
import { api } from '../lib/api';

interface LibImage {
  key: string;
  url: string;
  prev_url: string | null;
  source: string | null;
  updated_at: string;
}

/** Rasm qatori: taom, guruh yoki kategoriya */
interface Entry {
  key: string;
  name: string;
  sub: string;
  /** Kategoriya fon rasmi — keng (12:5) */
  wide?: boolean;
}

type View = 'items' | 'groups' | 'categories';

/** Kategoriya rasmining uzun tomoni (kesilmaydi) */
const WHOLE_MAX = 640;

/** Kichraytirish: taom va guruh — markazdan 512×512 ga kesiladi; kategoriya rasmi — kesilmaydi, butunligicha
 *  (nisbati saqlanadi, uzun tomoni 640 px gacha) — kassada to'liq ko'rinadi */
async function toImage(file: File, whole = false): Promise<{ data: string; type: string }> {
  const bitmap = await createImageBitmap(file);
  const fit = Math.min(1, WHOLE_MAX / Math.max(bitmap.width, bitmap.height));
  const w = whole ? Math.max(1, Math.round(bitmap.width * fit)) : 512;
  const h = whole ? Math.max(1, Math.round(bitmap.height * fit)) : 512;
  const scale = Math.min(bitmap.width / w, bitmap.height / h);
  const sw = w * scale;
  const sh = h * scale;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Rasm o‘qilmadi');
  ctx.drawImage(bitmap, (bitmap.width - sw) / 2, (bitmap.height - sh) / 2, sw, sh, 0, 0, w, h);
  bitmap.close();
  let type = 'image/webp';
  let url = canvas.toDataURL(type, 0.82);
  if (!url.startsWith('data:image/webp')) {
    type = 'image/jpeg';
    url = canvas.toDataURL(type, 0.85);
  }
  return { data: url.slice(url.indexOf(',') + 1), type };
}

export function LibraryPage() {
  const { data, error, loading, reload } = useAsync(() => api.select<LibImage>('library_images?select=key,url,prev_url,source,updated_at'), []);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string>();
  const [syncOpen, setSyncOpen] = useState(false);
  const [view, setView] = useState<View>('items');
  const rows = useMemo(() => new Map((data ?? []).map((i) => [i.key, i])), [data]);
  const images = useMemo(() => new Map((data ?? []).map((i) => [i.key, i.url])), [data]);
  const missing = LIBRARY_ITEMS.filter((i) => !images.has(i.key));
  const groupEntries: Entry[] = LIBRARY_GROUPS.map((g) => ({
    key: g.key,
    name: g.name,
    sub: `${libraryCategory(g.category)?.name ?? g.category} · ${g.items.map((i) => i.variant).join(' / ')}`,
  }));
  const categoryEntries: Entry[] = LIBRARY_CATEGORIES.map((c) => ({
    key: libraryCategoryImageKey(c.key),
    name: `${c.icon} ${c.name}`,
    sub: `${LIBRARY_ITEMS.filter((i) => i.category === c.key).length} ta taom`,
    wide: true,
  }));
  const counts = {
    items: missing.length,
    groups: groupEntries.filter((e) => !images.has(e.key)).length,
    categories: categoryEntries.filter((e) => !images.has(e.key)).length,
  };

  const upload = async (item: Entry, file: File) => {
    setBusy(item.key);
    setActionError(undefined);
    try {
      const img = await toImage(file, item.wide);
      await api.invoke('library-image', { key: item.key, image: img.data, content_type: img.type });
      reload();
    } catch (e) {
      setActionError(`${item.name}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (item: Entry) => {
    setBusy(item.key);
    setActionError(undefined);
    try {
      await api.invoke('library-image', { key: item.key, remove: true });
      reload();
    } catch (e) {
      setActionError(`${item.name}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const revert = async (item: Entry) => {
    setBusy(item.key);
    setActionError(undefined);
    try {
      await api.invoke('library-image', { key: item.key, revert: true });
      reload();
    } catch (e) {
      setActionError(`${item.name}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const row = (e: Entry) => (
    <LibRow
      key={e.key}
      entry={e}
      url={images.get(e.key) ?? null}
      hasPrev={Boolean(rows.get(e.key)?.prev_url)}
      busy={busy === e.key}
      onUpload={(f) => void upload(e, f)}
      onRemove={() => void remove(e)}
      onRevert={() => void revert(e)}
    />
  );

  return (
    <>
      <header className="cn-page-head">
        <div>
          <h1>Menyu kutubxonasi</h1>
          <p>
            Restoranlar “Tayyor ro‘yxatdan qo‘shish” orqali oladigan taomlar. Bu yerdagi rasm — umumiy: o‘z rasmini qo‘ymagan har bir
            restoranda chiqadi.
          </p>
        </div>
      </header>

      <Tabs<View>
        label="Kutubxona bo‘limlari"
        value={view}
        onChange={setView}
        tabs={[
          { key: 'items', label: 'Taomlar', count: LIBRARY_ITEMS.length },
          { key: 'groups', label: 'Guruh rasmlari', count: groupEntries.length },
          { key: 'categories', label: 'Kategoriya fonlari', count: categoryEntries.length },
        ]}
      />
      <div className="cn-toolbar">
        <span className="cn-muted">
          {view === 'items'
            ? 'Taom rasmi kassadagi tugmada va agregator menyusida chiqadi.'
            : view === 'groups'
              ? 'O‘lchamli taomlar kassada bitta kartada — shu kartaning rasmi. Rasm bo‘lmasa, birinchi o‘lcham rasmi chiqadi.'
              : 'Kassadagi kategoriya tugmasidagi rasm (rasmlar yoqilganda). Kesilmaydi — butunligicha ko‘rinadi; oq fonli rasm eng chiroyli chiqadi.'}{' '}
          <b>Rasm kerak: {counts[view]}</b>
        </span>
        <div className="cn-row" style={{ gap: 8 }}>
          <label className="cn-check">
            <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} /> Faqat rasm kerak
          </label>
          {view === 'items' && (
            <Button variant="primary" onClick={() => setSyncOpen(true)} disabled={!data}>
              <Icon name="refresh" /> {LIBRARY_SOURCE_RESTAURANT.name}’dan yangilash
            </Button>
          )}
        </div>
      </div>
      {(error || actionError) && <Alert tone="danger">{error?.message ?? actionError}</Alert>}
      {loading && !data ? (
        <Spinner />
      ) : view === 'items' ? (
        LIBRARY_PACKS.map((pack) => (
          <section key={pack.key} className="cn-lib-pack">
            <h2>{pack.name}</h2>
            {LIBRARY_CATEGORIES.filter((c) => c.pack === pack.key).map((c) => {
              const items = LIBRARY_ITEMS.filter((i) => i.category === c.key && (!onlyMissing || !images.has(i.key)));
              if (items.length === 0) return null;
              return (
                <Card key={c.key} title={`${c.icon} ${c.name}`}>
                  <ul className="cn-lib">
                    {items.map((i) => {
                      const e: Entry = { key: i.key, name: i.name, sub: i.key };
                      return row(e);
                    })}
                  </ul>
                </Card>
              );
            })}
          </section>
        ))
      ) : (
        <Card title={view === 'groups' ? 'Guruh rasmlari' : 'Kategoriya fon rasmlari'}>
          <ul className="cn-lib">{(view === 'groups' ? groupEntries : categoryEntries).filter((e) => !onlyMissing || !images.has(e.key)).map(row)}</ul>
        </Card>
      )}
      {syncOpen && data && <SyncModal rows={rows} onClose={() => setSyncOpen(false)} onDone={reload} />}
    </>
  );
}

interface SourceItem {
  id: string;
  name: string;
  is_active: boolean;
  image_url: string | null;
  source: string | null;
}

type SyncState = 'new' | 'same' | 'none';

/** Manba restoran rasmlari bilan solishtirish: yangisi oldindan belgilangan, bitta tugma bilan hammasi */
function SyncModal({ rows, onClose, onDone }: { rows: ReadonlyMap<string, LibImage>; onClose: () => void; onDone: () => void }) {
  const ids = useMemo(() => [...new Set(Object.values(LIBRARY_SOURCE_PRODUCTS))], []);
  const preview = useAsync(() => api.invoke<{ items: SourceItem[] }>('library-image', { preview: ids }), [ids]);
  const list = useMemo(() => {
    const byId = new Map((preview.data?.items ?? []).map((p) => [p.id, p]));
    return LIBRARY_ITEMS.filter((i) => LIBRARY_SOURCE_PRODUCTS[i.key]).map((item) => {
      const src = byId.get(LIBRARY_SOURCE_PRODUCTS[item.key] ?? '') ?? null;
      const state: SyncState = !src?.source ? 'none' : rows.get(item.key)?.source === src.source ? 'same' : 'new';
      return { item, src, state };
    });
  }, [preview.data, rows]);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const chosen = picked ?? new Set(list.filter((r) => r.state === 'new').map((r) => r.item.key));
  const [progress, setProgress] = useState<{ done: number; total: number; failed: string[] } | null>(null);
  const running = progress !== null && progress.done < progress.total;
  const finished = progress !== null && progress.done === progress.total;

  const toggle = (key: string) => {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPicked(next);
  };

  const run = async () => {
    const todo = list.filter((r) => chosen.has(r.item.key) && r.src?.source);
    const failed: string[] = [];
    setProgress({ done: 0, total: todo.length, failed });
    for (const [n, r] of todo.entries()) {
      try {
        await api.invoke('library-image', { key: r.item.key, source_product: r.src?.id });
      } catch {
        failed.push(r.item.name);
      }
      setProgress({ done: n + 1, total: todo.length, failed: [...failed] });
    }
    onDone();
  };

  const fresh = list.filter((r) => r.state === 'new').length;
  return (
    <Modal
      open
      wide
      tall
      busy={running}
      title={`${LIBRARY_SOURCE_RESTAURANT.name}’dan yangilash`}
      onClose={onClose}
      footer={
        finished ? (
          <Button variant="primary" onClick={onClose}>
            Yopish
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={running}>
              Bekor qilish
            </Button>
            <Button variant="primary" onClick={() => void run()} disabled={running || chosen.size === 0 || preview.loading}>
              {running ? `Yangilanmoqda… ${progress?.done ?? 0} / ${progress?.total ?? 0}` : `Tanlanganlarni yangilash (${chosen.size})`}
            </Button>
          </>
        )
      }
    >
      <p className="cn-muted" style={{ marginTop: 0 }}>
        Restoran rasmlari kutubxonaga nusxa qilinadi — {LIBRARY_SOURCE_RESTAURANT.name} menyusida hech narsa o‘zgarmaydi. Logo yoki
        reklama yozuvi bor rasmni belgidan chiqaring. Oldingi rasm saqlanadi va “Oldingisi” bilan qaytariladi.
      </p>
      {progress && (
        <Alert tone={progress.failed.length ? 'warning' : finished ? 'success' : 'info'}>
          Yangilandi: {progress.done - progress.failed.length} / {progress.total}
          {progress.failed.length > 0 && ` · bo‘lmadi: ${progress.failed.join(', ')}`}
        </Alert>
      )}
      {preview.error && <Alert tone="danger">{preview.error.message}</Alert>}
      {preview.loading ? (
        <Spinner />
      ) : (
        <>
          <div className="cn-row" style={{ gap: 8, margin: '8px 0', flexWrap: 'wrap' }}>
            <span className="cn-muted">
              {list.length} ta taom · yangi rasm: <b>{fresh}</b> · tanlangan: <b>{chosen.size}</b>
            </span>
            <Button size="sm" variant="ghost" disabled={running} onClick={() => setPicked(new Set(list.filter((r) => r.state !== 'none').map((r) => r.item.key)))}>
              Hammasini belgilash
            </Button>
            <Button size="sm" variant="ghost" disabled={running} onClick={() => setPicked(new Set())}>
              Belgini olish
            </Button>
          </div>
          <ul className="cn-lib cn-lib-sync">
            {list.map(({ item, src, state }) => {
              const cur = rows.get(item.key)?.url ?? null;
              return (
                <li key={item.key} className="cn-lib__row">
                  <input
                    type="checkbox"
                    className="cn-lib-sync__check"
                    checked={chosen.has(item.key)}
                    disabled={state === 'none' || running || finished}
                    onChange={() => toggle(item.key)}
                    aria-label={`${item.name}: yangilash`}
                  />
                  <span className="cn-lib__img cn-lib-sync__img" title="Hozirgi">
                    {cur ? <img src={cur} alt={`${item.name}: hozirgi rasm`} loading="lazy" /> : <Icon name="grid" />}
                  </span>
                  <Icon name="chevron" />
                  <span className="cn-lib__img cn-lib-sync__img" title="Yangi">
                    {src?.image_url ? <img src={src.image_url} alt={`${item.name}: yangi rasm`} loading="lazy" /> : <Icon name="grid" />}
                  </span>
                  <span className="cn-lib__text">
                    <b>{item.name}</b>
                    <span className="cn-muted">Manba: {src?.name ?? 'topilmadi'}</span>
                  </span>
                  {state === 'new' ? <Badge tone="info">yangi rasm</Badge> : state === 'same' ? <Badge tone="success">bir xil</Badge> : <Badge tone="warning">rasm yo‘q</Badge>}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Modal>
  );
}

function LibRow({
  entry: item,
  url,
  hasPrev,
  busy,
  onUpload,
  onRemove,
  onRevert,
}: {
  entry: Entry;
  url: string | null;
  hasPrev: boolean;
  busy: boolean;
  onUpload: (f: File) => void;
  onRemove: () => void;
  onRevert: () => void;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  return (
    <li className="cn-lib__row">
      <span className={`cn-lib__img${item.wide ? ' cn-lib__img--wide' : ''}`}>{url ? <img src={url} alt="" loading="lazy" /> : <Icon name="grid" />}</span>
      <span className="cn-lib__text">
        <b>{item.name}</b>
        <span className="cn-muted">{item.sub}</span>
      </span>
      {url ? <Badge tone="success">rasm bor</Badge> : <Badge tone="warning">rasm kerak</Badge>}
      <span className="cn-row" style={{ gap: 6 }}>
        <Button size="sm" onClick={() => input.current?.click()} disabled={busy}>
          {busy ? '…' : url ? 'Almashtirish' : 'Yuklash'}
        </Button>
        {url && hasPrev && (
          <Button size="sm" variant="ghost" onClick={onRevert} disabled={busy} title="Oldingi rasmga qaytarish">
            Oldingisi
          </Button>
        )}
        {url && (
          <Button size="sm" variant="ghost" onClick={onRemove} disabled={busy} aria-label={`${item.name}: rasmni olib tashlash`}>
            <Icon name="trash" />
          </Button>
        )}
      </span>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onUpload(f);
        }}
      />
    </li>
  );
}
